// FILE: src/components/session/SessionReviewList.js
//
// The question-by-question review list — the ONE implementation shared by the
// screen shown the moment a session ends (ResultsPage) and the reopenable
// review of a past attempt (SessionResultsView, ?view=results&session=<id>).
//
// It renders a normalized review model (see reviewModel.js) so it never needs
// to know whether the data came from live session state or from the stored
// attempt. It is strictly READ-ONLY: nothing here can change an answer, a
// score, or the session.
//
// Per item it shows the full stem, every answer choice with the learner's
// selection and the correct answer marked, the explanation, time spent, and —
// for a historical attempt — whether the question has been edited or re-keyed
// since (see contentStatus on the model).
import React, { useState } from 'react';
import { CaseReviewHeader } from '../case-studies/exam/CaseReviewGroup';
import rcss from './ResultsPage.module.css';

export const STATUS_CFG = {
  correct:    { label: 'Correct',    color: 'var(--good)', bg: 'var(--good-soft)', icon: '✓' },
  incorrect:  { label: 'Incorrect',  color: 'var(--bad)',  bg: 'var(--bad-soft)',  icon: '✗' },
  skipped:    { label: 'Skipped',    color: 'var(--warn)', bg: 'var(--warn-soft)', icon: '→' },
  unanswered: { label: 'Unanswered', color: 'var(--muted)', bg: 'var(--bg)',       icon: '—' },
};

/** mm:ss for a per-question duration; null when nothing was recorded. */
export function fmtSeconds(sec) {
  if (typeof sec !== 'number' || !isFinite(sec) || sec < 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Filter + sort. 'slowest' is a SORT over every item that has recorded timing
 * (longest first, question order as the stable tie-breaker); items with no
 * timing are appended in order rather than dropped.
 */
export function applyFilter(items, filter) {
  if (filter === 'slowest') {
    const timed = items.filter((i) => typeof i.timeSpentSeconds === 'number');
    const untimed = items.filter((i) => typeof i.timeSpentSeconds !== 'number');
    timed.sort((a, b) => (b.timeSpentSeconds - a.timeSpentSeconds) || (a.index - b.index));
    return [...timed, ...untimed];
  }
  if (filter === 'flagged') return items.filter((i) => i.wasFlagged === true);
  if (filter === 'all') return items;
  return items.filter((i) => i.status === filter);
}

const styles = {
  filterRow: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  filterButton: (isActive, color) => ({
    padding: '6px 14px',
    borderRadius: '20px',
    border: `2px solid ${isActive ? color : 'var(--line-strong)'}`,
    background: isActive ? color : '#fff',
    color: isActive ? '#fff' : 'var(--muted)',
    fontSize: '0.82rem',
    fontWeight: isActive ? '700' : 'normal',
    cursor: 'pointer',
    transition: 'all 0.12s',
  }),
  reviewList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '32px' },
  emptyMessage: { textAlign: 'center', color: 'var(--line-strong)', padding: '32px', fontStyle: 'italic' },
  reviewItem: (borderColor) => ({
    background: '#fff',
    border: `1px solid ${borderColor}`,
    borderLeft: `4px solid ${borderColor}`,
    borderRadius: '6px',
    overflow: 'hidden',
    cursor: 'pointer',
  }),
  reviewItemHeader: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px' },
  qNumber: { fontSize: '0.78rem', color: 'var(--line-strong)', fontWeight: 'bold', minWidth: '26px', paddingTop: '3px', flexShrink: 0 },
  refIdBadge: {
    fontFamily: "'Consolas', 'Monaco', monospace", fontSize: '0.68rem', color: 'var(--line-strong)',
    background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: '3px', padding: '1px 5px',
    flexShrink: 0, alignSelf: 'center', letterSpacing: '0.3px',
  },
  statusBadge: (color, bg) => ({
    display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: '12px',
    fontSize: '0.76rem', fontWeight: '700', color, background: bg,
    border: `1px solid ${color}`, whiteSpace: 'nowrap', flexShrink: 0,
  }),
  metaChip: {
    fontSize: '0.72rem', color: 'var(--muted)', background: 'var(--bg)', border: '1px solid var(--line)',
    borderRadius: '10px', padding: '1px 8px', whiteSpace: 'nowrap', flexShrink: 0, alignSelf: 'center',
  },
  questionPreview: {
    fontSize: '0.92rem', color: 'var(--ink)', lineHeight: '1.45', flex: 1, overflow: 'hidden',
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
  },
  chevron: { fontSize: '0.7rem', color: 'var(--line-strong)', flexShrink: 0, paddingTop: '4px' },
  reviewDetail: { padding: '0 14px 16px 50px', borderTop: '1px solid var(--line)' },
  fullQuestion: { fontSize: '0.93rem', color: 'var(--ink)', lineHeight: '1.6', margin: '12px 0' },
  exhibit: { maxWidth: '100%', borderRadius: '6px', border: '1px solid var(--line)', margin: '0 0 12px' },
  choiceRow: (state) => ({
    display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '7px 10px', borderRadius: '5px',
    fontSize: '0.875rem', lineHeight: '1.45',
    background: state === 'correct' ? 'var(--good-soft)' : state === 'chosen' ? 'var(--bad-soft)' : 'transparent',
    border: `1px solid ${state === 'correct' ? 'var(--good)' : state === 'chosen' ? 'var(--bad)' : 'var(--line)'}`,
  }),
  choiceId: { fontWeight: '700', minWidth: '18px', flexShrink: 0 },
  choiceTag: { fontSize: '0.72rem', fontWeight: '700', whiteSpace: 'nowrap', flexShrink: 0 },
  answerGrid: { display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '0.875rem', marginTop: '10px' },
  answerRow: { display: 'flex', gap: '8px' },
  answerRowLabel: { color: 'var(--muted)', fontWeight: '600', whiteSpace: 'nowrap', minWidth: '110px', flexShrink: 0 },
  answerRowValue: (color) => ({ color: color || 'var(--ink)', fontWeight: color ? '600' : 'normal', lineHeight: '1.45' }),
  explanation: {
    marginTop: '12px', padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--line)',
    borderRadius: '6px', fontSize: '0.86rem', lineHeight: '1.6', color: 'var(--ink)', whiteSpace: 'pre-wrap',
  },
  explanationLabel: { fontWeight: '700', color: 'var(--muted)', marginRight: '6px' },
  notice: {
    marginTop: '10px', padding: '8px 10px', background: 'var(--warn-soft)', border: '1px solid var(--warn)',
    borderRadius: '6px', fontSize: '0.8rem', lineHeight: '1.5', color: 'var(--ink)',
  },
  unavailable: { fontStyle: 'italic', color: 'var(--muted)', margin: '12px 0', fontSize: '0.9rem' },
  metaLine: { marginTop: '10px', fontSize: '0.76rem', color: 'var(--muted)' },
};

const reportLinkStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '10px', padding: '5px 12px',
  background: 'transparent', border: '1px solid var(--line)', borderRadius: '5px', cursor: 'pointer',
  fontSize: '0.8rem', color: 'var(--line-strong)', transition: 'color 0.1s, border-color 0.1s',
};

/** Renders one id (or 'prompt:option' pair) as readable text. */
export function choiceLabel(question, id) {
  const choices = question?.choices;
  if (Array.isArray(choices)) {
    const c = choices.find((ch) => ch.id === id);
    return c ? `${c.id}. ${c.text}` : String(id);
  }
  const c = choices || {};
  if (String(id).includes(':')) {
    const [p, o] = String(id).split(':');
    const pt = (c.prompts || []).find((x) => String(x.id) === p);
    const ot = (c.options || []).find((x) => String(x.id) === o);
    return `${pt ? pt.text : p} → ${ot ? ot.text : o}`;
  }
  const pool = [...(c.items || []), ...(c.options || []), ...(c.prompts || [])];
  const m = pool.find((x) => String(x.id) === String(id));
  return m ? m.text : String(id);
}

export function ReviewItem({ item, isExpanded, onToggle, onReport }) {
  const { index, question, selectedAnswers = [], status } = item;
  const cfg = STATUS_CFG[status] || STATUS_CFG.unanswered;
  const correctAnswers = Array.isArray(question?.correctAnswers) ? question.correctAnswers : [];
  const referenceId = item.referenceId || question?.referenceId || null;
  const timeLabel = fmtSeconds(item.timeSpentSeconds);
  const available = item.contentStatus !== 'unavailable' && !!question;
  const stem = available ? question.question : null;

  const correctText = correctAnswers.map((id) => choiceLabel(question, id)).join(' | ');
  const selectedText = selectedAnswers.length > 0
    ? selectedAnswers.map((id) => choiceLabel(question, id)).join(' | ')
    : '—';
  const listChoices = available && Array.isArray(question.choices) ? question.choices : null;

  const meta = available
    ? [question.ecoDomain, question.performanceDomain, question.category, question.ecoTask]
      .filter((v) => typeof v === 'string' && v.trim()).slice(0, 2)
    : [];

  return (
    <div
      data-testid="results-item"
      data-status={status}
      data-content-status={item.contentStatus || 'live'}
      style={styles.reviewItem(cfg.color)}
      onClick={onToggle}
    >
      <div style={styles.reviewItemHeader} className={rcss.reviewItemHeader}>
        <span style={styles.qNumber}>Q{index + 1}</span>
        <span style={styles.statusBadge(cfg.color, cfg.bg)}>
          {cfg.icon} {cfg.label}
        </span>
        {item.wasFlagged && (
          <span data-testid="results-item-flagged" style={styles.metaChip} title="You flagged this question">⚑ Flagged</span>
        )}
        {timeLabel && (
          <span data-testid="results-item-time" style={styles.metaChip} title="Time spent on this question">⏱ {timeLabel}</span>
        )}
        {referenceId && (
          <span data-testid="results-reference-id" style={styles.refIdBadge}>{referenceId}</span>
        )}
        <span style={styles.questionPreview}>
          {stem || 'This question is no longer available.'}
        </span>
        <span style={styles.chevron}>{isExpanded ? '▲' : '▼'}</span>
      </div>

      {isExpanded && (
        <div style={styles.reviewDetail} className={rcss.reviewDetail}>
          {!available && (
            <p data-testid="results-item-unavailable" style={styles.unavailable}>
              This question is no longer in the question bank, so it can’t be shown. Your answer and
              result for it are still counted in your score.
            </p>
          )}

          {available && <div style={styles.fullQuestion}>{stem}</div>}

          {available && question.image?.src && (
            <img src={question.image.src} alt={question.image.alt || ''} style={styles.exhibit} />
          )}

          {listChoices && (
            <div data-testid="results-item-choices" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {listChoices.map((ch) => {
                const isCorrect = correctAnswers.includes(ch.id);
                const isChosen = selectedAnswers.includes(ch.id);
                const state = isCorrect ? 'correct' : isChosen ? 'chosen' : 'none';
                return (
                  <div key={ch.id} style={styles.choiceRow(state)} data-choice-state={state}>
                    <span style={styles.choiceId}>{ch.id}.</span>
                    <span style={{ flex: 1 }}>{ch.text}</span>
                    {isCorrect && (
                      <span style={{ ...styles.choiceTag, color: 'var(--good)' }}>✓ Correct answer</span>
                    )}
                    {isChosen && !isCorrect && (
                      <span style={{ ...styles.choiceTag, color: 'var(--bad)' }}>Your answer</span>
                    )}
                    {isChosen && isCorrect && (
                      <span style={{ ...styles.choiceTag, color: 'var(--good)' }}>· Your answer</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Always present, in words — also covers ordering / matching types,
              where the choices are not a simple list. */}
          <div style={styles.answerGrid}>
            {(status === 'correct' || status === 'incorrect') && (
              <div style={styles.answerRow}>
                <span style={styles.answerRowLabel} className={rcss.answerRowLabel}>Your answer:</span>
                <span style={styles.answerRowValue(status === 'incorrect' ? 'var(--bad)' : 'var(--good)')}>
                  {selectedText}
                </span>
              </div>
            )}
            {available && (
              <div style={styles.answerRow}>
                <span style={styles.answerRowLabel} className={rcss.answerRowLabel}>Correct answer:</span>
                <span style={styles.answerRowValue('var(--good)')}>{correctText || '—'}</span>
              </div>
            )}
          </div>

          {available && question.explanation && (
            <div data-testid="results-item-explanation" style={styles.explanation}>
              <span style={styles.explanationLabel}>Why:</span>{question.explanation}
            </div>
          )}

          {item.keyChanged && (
            <div data-testid="results-item-key-changed" style={styles.notice}>
              The answer key for this question has changed since you took this exam. You are seeing the
              key your answer was graded against; the current key is{' '}
              <strong>{(item.currentKey || []).join(', ') || '—'}</strong>.
            </div>
          )}
          {item.contentStatus === 'reconstructed' && item.contentChanged && (
            <div data-testid="results-item-reconstructed" style={styles.notice}>
              This question has been edited since your attempt. It is shown here as it appeared when you
              answered it.
            </div>
          )}
          {item.contentStatus === 'unverified' && (
            <div data-testid="results-item-unverified" style={styles.notice}>
              This question was edited after your attempt and we can’t confirm its original wording, so the
              current version is shown. Your answer and result are unchanged.
            </div>
          )}

          {meta.length > 0 && (
            <div style={styles.metaLine}>{meta.join(' · ')}</div>
          )}

          {onReport && available && (
            <button
              data-testid="results-report-button"
              style={reportLinkStyle}
              onClick={(e) => { e.stopPropagation(); onReport(question, selectedAnswers); }}
              aria-label={`Report question ${referenceId || question.id}`}
            >
              ⚑ Report this question
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * @param {object}   props
 * @param {object[]} props.items        normalized review items
 * @param {object}   props.counts       { correct, incorrect, skipped, unanswered, total }
 * @param {object}   [props.caseBlocks] case-study blocks, keyed by case id (live sessions)
 * @param {object}   [props.caseStats]  per-case score stats
 * @param {function} [props.onReport]   report-a-question handler (omitted → no button)
 * @param {function} [props.onFilterChange] called with the filter key when it changes
 */
export default function SessionReviewList({
  items, counts, caseBlocks, caseStats, onReport, onFilterChange,
}) {
  const [filter, setFilter] = useState('all');
  const [expandedItems, setExpandedItems] = useState(new Set());

  const anyTiming = items.some((i) => typeof i.timeSpentSeconds === 'number');
  const flaggedCount = items.filter((i) => i.wasFlagged === true).length;

  const FILTERS = [
    { key: 'all',        label: `All (${counts.total})`,             color: 'var(--ink)' },
    { key: 'correct',    label: `Correct (${counts.correct})`,       color: 'var(--good)' },
    { key: 'incorrect',  label: `Incorrect (${counts.incorrect})`,   color: 'var(--bad)' },
    { key: 'skipped',    label: `Skipped (${counts.skipped})`,       color: 'var(--warn)' },
    { key: 'unanswered', label: `Unanswered (${counts.unanswered})`, color: 'var(--muted)' },
    ...(anyTiming ? [{ key: 'slowest', label: 'Slowest', color: 'var(--ink)' }] : []),
    ...(flaggedCount > 0 ? [{ key: 'flagged', label: `Flagged (${flaggedCount})`, color: 'var(--warn)' }] : []),
  ];

  function pickFilter(key) {
    setFilter(key);
    if (key !== filter) onFilterChange?.(key);
  }

  function toggleItem(index) {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  }

  const visible = applyFilter(items, filter);

  // Case-study blocks keep their group header, but only in their natural order.
  const grouped = visible.map((item, i) => {
    const caseId = item.caseStudyId || item.question?.caseStudyId || null;
    const prev = visible[i - 1];
    const prevCaseId = prev ? (prev.caseStudyId || prev.question?.caseStudyId || null) : null;
    return { item, caseHeader: caseId && caseId !== prevCaseId && filter !== 'slowest' ? { caseId } : null };
  });

  return (
    <>
      <div style={styles.filterRow} className={rcss.filterRow}>
        {FILTERS.map(({ key, label, color }) => (
          <button
            key={key}
            data-testid={`results-filter-${key}`}
            style={styles.filterButton(filter === key, color)}
            onClick={() => pickFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {filter === 'slowest' && (
        <p data-testid="results-slowest-note" style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '-10px 0 14px' }}>
          Sorted by time spent, longest first.
        </p>
      )}

      <div style={styles.reviewList}>
        {grouped.length === 0 ? (
          <p style={styles.emptyMessage}>No questions match this filter.</p>
        ) : (
          grouped.map(({ item, caseHeader }) => (
            <React.Fragment key={item.index}>
              {caseHeader && caseBlocks && caseBlocks[caseHeader.caseId] && (
                <CaseReviewHeader
                  block={caseBlocks[caseHeader.caseId]}
                  stats={caseStats ? caseStats[caseHeader.caseId] : undefined}
                />
              )}
              <ReviewItem
                item={item}
                isExpanded={expandedItems.has(item.index)}
                onToggle={() => toggleItem(item.index)}
                onReport={onReport}
              />
            </React.Fragment>
          ))
        )}
      </div>
    </>
  );
}
